<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name: 'databoxData2', position: 'top', allowOtherLeft: true }"
    :button-bind="{ icon, tooltip, verboseName: tooltip }"
    unfill-width
    has-close-header
    documentation-position="header"
  >
    <template #[`content.header.prepend`]>
      <v-col cols="auto" class="text-body-2 font-weight-medium pr-2 tw:pt-0.5">{{ tooltip }}</v-col>
    </template>
    <template #content>
      <v-row no-gutters class="pa-3">
        <v-col cols="12">
          <v-row no-gutters align="center">
            <v-col cols="auto">
              <dots-menu
                :items="dotsItems"
                @multiple="turnOnDrawing('multiple')"
                @freehand="turnOnDrawing('freehand')"
                @single="turnOnDrawing('single')"
              >
                <template #activator="{ on }">
                  <base-button
                    type="alternative"
                    :toggled="isDrawing"
                    v-on="getButtonListeners(on)"
                    :disabled="isDrawn || loading"
                  >
                    {{ $i18n.t('sidebar.searchArea.selectArea') }}
                  </base-button>
                </template>
              </dots-menu>
              <base-button
                v-if="isDrawn || selectedFeatures?.length"
                type="text"
                color="error"
                icon="mdi-cancel"
                :disabled="loading"
                @click="clearSelection"
              />
            </v-col>
            <v-col cols="auto" class="ml-2" style="min-width: 250px">
              <data-input v-model="payload.layer" v-bind="availableDataInput" />
            </v-col>
            <v-col cols="auto" class="ml-2">
              <base-button
                icon="mdi-layers-plus"
                type="alternative"
                @click="run"
                :disabled="!isGeomValid || !payload.layer"
                :loading="loading"
                :tooltip="
                  !isGeomValid || !payload.layer
                    ? $i18n.t('rules.selectAreaAndChooseData')
                    : $i18n.t('sidebar.databoxData.addAsTemporaryLayer')
                "
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </navbar-menu-tool>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import NavbarMenuTool from '@/components/NavbarMenuTool';

import { createBox } from 'ol/interaction/Draw';
import { transformExtent } from 'ol/proj';

export default {
  name: 'TheNavbarToolsPanelDataboxDataTwo',
  components: { DataInput, DotsMenu, NavbarMenuTool },
  props: {
    icon: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
    },
  },
  computed: {
    selectedFeaturesLayers: get('map/selectedFeatures'),
    availableDataInput() {
      return {
        dataType: { name: 'select' },
        items: this.sortedAvailableDatas,
        itemText: 'verboseName',
        itemValue: 'name',
        readonly: this.loading,
        label: this.$i18n.t('sidebar.databoxData.data'),
        hideDetails: true,
        rules: [v => !!v || ''],
        loading: !this.fetched,
      };
    },
    sortedAvailableDatas() {
      return this.$_sortObjectsByAttribute(this.availableDatas, 'verboseName');
    },
  },
  data: vm => ({
    availableDatas: [],
    dotsItems: [
      { name: 'multiple' },
      { name: 'freehand' },
      { name: 'single', customName: vm.$i18n.t('menu.selectFeatures') },
    ],
    fetched: false,
    loading: false,
    isDrawn: false,
    isDrawing: false,
    isGeomValid: false,
    isMenuOpen: false,
    menuOpenWatch: undefined,

    payload: {
      bbox: null,
      geojson: null,
      layer: null,
    },
    selectedFeatures: [],
  }),
  methods: {
    getDataboxData: call('externalSystems/getDataboxData'),
    getDbGisBoxMetadata: call('externalSystems/getDbGisBoxMetadata'),
    async init() {
      this.menuOpenWatch();
      const r = await this.getDbGisBoxMetadata();
      this.availableDatas = r.map(data => {
        return {
          name: data.name,
          verboseName: data.title,
          style: data.style || null,
        };
      });
      this.fetched = true;
    },
    async run() {
      this.loading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.payload));
        const dbLayerName = payload.layer;
        if (this.selectedFeatures.length) {
          payload.layers = [];
          this.selectedFeatures.forEach(feature => {
            const l = payload.layers.find(layer => layer.layer_id === feature.layerId);
            if (!l) payload.layers.push({ layer_id: feature.layerId, ids: [feature.id] });
            else l.ids.push(feature.id);
          });
        }
        if (!payload.bbox) delete payload.bbox;
        if (!payload.geojson) delete payload.geojson;
        payload.databox_layers = [dbLayerName];
        delete payload.layer;

        const data = await this.getDataboxData(payload);
        const result = data[dbLayerName];

        const dbLayerMetadata = this.availableDatas.find(d => d.name === dbLayerName) || {};
        const { style = null, verboseName = '' } = dbLayerMetadata;

        this.$root.$emit('addTemporaryLayer', {
          geometry: result,
          name: verboseName || dbLayerName,
          style,
          labelsVisible: true,
        });
      } finally {
        this.loading = false;
      }
    },
    getButtonListeners(on) {
      return this.isDrawing ? { click: this.turnOffSelecting } : on;
    },

    turnOffSelecting() {
      if (this.selectedFeatures.length) this.isDrawn = true;
      this.isDrawing = false;
      this.$root.$emit('selection-action');
    },

    turnOnDrawing(type) {
      this.isDrawing = true;
      if (type === 'single') {
        this.$root.$emit('identification-action', true, { specialType: 'addByMap' });
        this.$root.$off('assignedObjects');
        this.$root.$on('assignedObjects', identifiedFeatures => {
          const features = identifiedFeatures.map(feature => feature.features);
          for (const feature of features.flat()) {
            const { dataSource = '', layerId, id: featureId } = feature;
            if (!dataSource) continue;
            const isFeatureSelected = this.selectedFeatures.some(el => el.layerId === layerId && el.id === featureId);
            if (isFeatureSelected) this.deleteFeature(feature);
            else {
              this.selectedFeatures.push(feature);
              this.$store.set('map/ADD_SELECTED_FEATURES!', {
                type: 'selectedFeatures',
                features: {
                  [layerId]: [featureId],
                },
              });
              this.$root.$emit('setProjectLayerStyle', layerId);
            }
          }
        });
      } else {
        this.$root.$emit('drawSidebarGeometry', {
          drawendCallback: e => this.onDrawend(e, type),
          geometryFunction: type === 'multiple' ? createBox() : null,
          geometryType: 'polygon',
          isInitSnapping: false,
          freehand: type === 'freehand' ? true : false,
          ...(type === 'multiple' ? { drawType: 'Circle' } : {}),
        });
      }
    },
    onDrawend(e, type) {
      this.isDrawing = false;
      this.isDrawn = true;
      if (type === 'multiple') {
        this.payload.bbox = transformExtent(
          e.feature.getGeometry().getExtent(),
          this.$_config.defaultEpsg,
          'EPSG:4326'
        );
      }
      if (type === 'freehand') {
        this.payload.geojson = {
          type: 'Polygon',
          crs: {
            type: 'name',
            properties: { name: this.$_config.defaultEpsg || 'EPSG:4326' },
          },
          coordinates: e.feature.getGeometry().getCoordinates(),
        };
      }
    },

    clearSelection() {
      this.isDrawing = false;
      this.isDrawn = false;
      this.payload.bbox = null;
      this.payload.geojson = null;
      if (this.selectedFeatures.length) this.deleteFeatures();
      this.$root.$emit('clearSidebarGeometry');
      this.$root.$emit('identification-action', false);
    },
    deleteFeature({ layerId, id: featureId }) {
      if (featureId && layerId) {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'selectedFeatures',
          features: {
            [layerId]: [featureId],
          },
        });
      }
      const featureIndex = this.selectedFeatures.findIndex(ft => ft.id === featureId && ft.layerId === layerId);
      this.selectedFeatures.splice(featureIndex, 1);
    },
    deleteFeatures() {
      for (const layerId of Object.keys(this.selectedFeaturesLayers)) {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'selectedFeatures',
          features: {
            [layerId]: [],
          },
        });
      }
      this.selectedFeatures = [];
    },
  },
  watch: {
    'payload.bbox': {
      handler(nV) {
        this.isGeomValid = !!nV;
      },
    },
    'payload.geojson': {
      handler(nV) {
        this.isGeomValid = !!nV;
      },
    },
    selectedFeatures(nV) {
      this.isGeomValid = !!nV.length;
    },
    isMenuOpen(nV) {
      if (!nV) {
        this.loading = false;
        this.clearSelection();
      }
    },
  },
  mounted() {
    this.menuOpenWatch = this.$watch(
      vm => vm.isMenuOpen,
      nV => {
        if (nV) this.init();
      },
      { immediate: true }
    );
  },
};
</script>
